import type { TBrand } from "@/lib/branded";
import type {
  ApplianceId,
  CallTypeId,
  CivilityId,
  CompanyId,
  EscFolderId,
  EscFolderInfoId,
  EscFolderInvoiceId,
  EscFolderLogId,
  EscFolderSignatureId,
  EventCategoryId,
  EventPriorityId,
  EventStatusId,
  ExternalSiteId,
  GatewayShipmentModelTypeId,
  GatewayShipmentReasonId,
  HeatPumpBrandId,
  HeatPumpDeclarationId,
  HeatPumpModelId,
  MissionId,
  ModulatorCsId,
  ModulatorHwId,
  ModulatorLinkCsId,
  ModulatorLinkHwId,
  ModulatorLinkId,
  OperationId,
  OperationTypeId,
  OwnerStatusId,
  PersonId,
  PhotoId,
  PhotoTypeId,
  PilotCsId,
  PilotHwId,
  SerialNumber,
  SiteId,
  SiteLayoutId,
  SitePowerTypeId,
  SiteTypeId,
  SubscribedPowerId,
  SubscriberId,
  SubscriptionFailureReasonId,
  EscSubscriberSignatureId,
} from "@/types/common/ids";
import type { LocaleDate, LocaleDateTime } from "@/types/common/utils.ts";

import { z } from "zod";

import { SubscriptionFailureReasonCode } from "@/domains/subscription/subscription.codes.types";

/**
 * Helper to build a Zod schema for branded IDs.
 * Type argument **must** be explicitly provided.
 */
function brandedIdSchema<BRAND extends TBrand<number, any>>() {
  return z
    .number()
    .int()
    .positive()
    .transform((v) => v as BRAND);
}

function brandedStringSchema<BRAND extends TBrand<string, any>>(schema?: z.ZodString) {
  return (schema ?? z.string()).transform((v) => v as BRAND);
}

export const branded = {
  // String
  externalId: brandedStringSchema<ExternalSiteId>(), // Allow empty strings at input time for clearing
  serialNumber: brandedStringSchema<SerialNumber>(),
  subscriptionFailureReasonCode: brandedStringSchema<SubscriptionFailureReasonCode>(),

  // Date
  localeDate: brandedStringSchema<LocaleDate>(),
  localeDateTime: brandedStringSchema<LocaleDateTime>(),

  // Ids
  callTypeId: brandedIdSchema<CallTypeId>(),
  civilityId: brandedIdSchema<CivilityId>(),
  companyId: brandedIdSchema<CompanyId>(),
  eventCategoryId: brandedIdSchema<EventCategoryId>(),
  eventPriorityId: brandedIdSchema<EventPriorityId>(),
  eventStatusId: brandedIdSchema<EventStatusId>(),
  gatewayShipmentModelTypeId: brandedIdSchema<GatewayShipmentModelTypeId>(),
  gatewayShipmentReasonId: brandedIdSchema<GatewayShipmentReasonId>(),
  heatPumpBrandId: brandedIdSchema<HeatPumpBrandId>(),
  heatPumpDeclarationId: brandedIdSchema<HeatPumpDeclarationId>(),
  heatPumpModelId: brandedIdSchema<HeatPumpModelId>(),
  missionId: brandedIdSchema<MissionId>(),
  personId: brandedIdSchema<PersonId>(),
  photoId: brandedIdSchema<PhotoId>(),
  photoTypeId: brandedIdSchema<PhotoTypeId>(),
  escFolderId: brandedIdSchema<EscFolderId>(),
  escFolderInfoId: brandedIdSchema<EscFolderInfoId>(),
  escFolderInvoiceId: brandedIdSchema<EscFolderInvoiceId>(),
  escFolderLogId: brandedIdSchema<EscFolderLogId>(),
  escFolderSignatureId: brandedIdSchema<EscFolderSignatureId>(),
  escSubscriberSignatureId: brandedIdSchema<EscSubscriberSignatureId>(),
  operationId: brandedIdSchema<OperationId>(),
  operationTypeId: brandedIdSchema<OperationTypeId>(),
  ownerStatusId: brandedIdSchema<OwnerStatusId>(),
  siteId: brandedIdSchema<SiteId>(),
  siteLayoutId: brandedIdSchema<SiteLayoutId>(),
  sitePowerTypeId: brandedIdSchema<SitePowerTypeId>(),
  siteTypeId: brandedIdSchema<SiteTypeId>(),
  subscribedPowerId: brandedIdSchema<SubscribedPowerId>(),
  subscriberId: brandedIdSchema<SubscriberId>(),
  subscriptionFailureReasonId: brandedIdSchema<SubscriptionFailureReasonId>(),
  modulatorCsId: brandedIdSchema<ModulatorCsId>(),
  modulatorHwId: brandedIdSchema<ModulatorHwId>(),
  modulatorLinkCsId: brandedIdSchema<ModulatorLinkCsId>(),
  modulatorLinkHwId: brandedIdSchema<ModulatorLinkHwId>(),
  modulatorLinkId: brandedIdSchema<ModulatorLinkId>(),
  applianceId: brandedIdSchema<ApplianceId>(),
  pilotCsId: brandedIdSchema<PilotCsId>(),
  pilotHwId: brandedIdSchema<PilotHwId>(),
};
